import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { ReactNode } from 'react';

interface EditorAction {
  type: 'media' | 'emoji' | 'variable' | 'link' | 'discount' | 'contact' | 'alert';
  icon: ReactNode;
  label: string;
  isDisabled?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  onSelect?: (data?: any) => void;
  popoverContent?: ReactNode;
}

interface EditorToolbarProps {
  actions?: EditorAction[];
  actionGroups?: EditorAction[][]; // For grouping actions with dividers
}

export function SMSEditorToolbar({ actionGroups = [] }: EditorToolbarProps) {
  const renderAction = (action: EditorAction) => {
    const button = (
      <button
        className={cn(
          'w-6 h-6 rounded-md flex items-center justify-center transition-colors overflow-hidden',
          action.isDisabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:bg-accent hover:text-accent-foreground'
        )}
        onClick={(e) => {
          e.preventDefault();
          if (action.popoverContent) {
            action.onToggle?.();
          } else {
            action.onSelect?.();
          }
        }}
        disabled={action.isDisabled}
      >
        {action.icon}
      </button>
    );

    const tooltipButton = (
      <Tooltip>
        <TooltipTrigger asChild>
          {action.popoverContent ? (
            <div>
              <Popover
                key={action.type}
                open={action.isOpen}
                onOpenChange={action.onToggle}
                modal={false}
              >
                <PopoverTrigger asChild>{button}</PopoverTrigger>
                <PopoverContent
                  className="w-auto p-0 z-[100]"
                  align="start"
                  side="bottom"
                  sideOffset={5}
                >
                  {action.popoverContent}
                </PopoverContent>
              </Popover>
            </div>
          ) : (
            button
          )}
        </TooltipTrigger>
        <TooltipContent>
          <p>{action.label}</p>
        </TooltipContent>
      </Tooltip>
    );

    return tooltipButton;
  };

  return (
    <div className="flex justify-between items-center px-4 py-4 border-t bg-background rounded-b-lg">
      <div className="flex items-center">
        {actionGroups.map((group, groupIndex) => (
          <div key={groupIndex} className="flex items-center">
            <div className="flex gap-4">
              {group.map((action) => (
                <div key={action.type}>{renderAction(action)}</div>
              ))}
            </div>
            {groupIndex < actionGroups.length - 1 && <div className="mx-4 w-px h-5 bg-border" />}
          </div>
        ))}
      </div>
    </div>
  );
}
